import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, Loader2, Sparkles, ExternalLink } from 'lucide-react';
import { fetchAuthConfig, isLoggedIn, localLogin, startFacetLogin } from '../services/auth';
import type { AuthConfig } from '../services/auth';

export default function Login() {
  const [email, setEmail] = useState('admin@atelier.local');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoggedIn()) {
      navigate('/');
      return;
    }
    fetchAuthConfig()
      .then(setConfig)
      .catch(() => setConfig({ use_facet_auth: false }))
      .finally(() => setConfigLoading(false));
  }, [navigate]);

  const doLocalLogin = async () => {
    if (!email.trim() || !password) return;
    setLoading(true);
    setError('');
    try {
      await localLogin(email.trim(), password);
      window.location.href = '/';
    } catch (err) {
      const e = err as { response?: { data?: { detail?: unknown } } };
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : '登录失败，请检查邮箱和密码');
    } finally {
      setLoading(false);
    }
  };

  const doFacetLogin = async () => {
    if (!config) return;
    setLoading(true);
    setError('');
    try {
      await startFacetLogin(config);
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      setError(message || '启动 Facet 登录失败');
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--gradient-dark)',
        padding: '24px',
      }}
    >
      <div
        className="glass-card neon-border"
        style={{
          width: '100%',
          maxWidth: '420px',
          padding: '40px',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: 'var(--gradient-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            boxShadow: 'var(--glow-blue)',
          }}
        >
          <img src="/logo.svg" alt="Atelier" width="40" height="40" />
        </div>

        <h2 style={{ margin: '0 0 8px', fontSize: '24px' }}>
          <Sparkles size={20} style={{ verticalAlign: 'middle', marginRight: 6, color: 'var(--neon-purple)' }} />
          Atelier 琢玉坊
        </h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: '32px', fontSize: '14px' }}>
          Agent 能力教学与蒸馏系统
        </p>

        {configLoading ? (
          <div style={{ color: 'var(--text-muted)', padding: '24px' }}>
            <Loader2 size={24} className="spin" style={{ margin: '0 auto 12px' }} />
            加载登录配置...
          </div>
        ) : config?.use_facet_auth ? (
          <div className="flex flex-col" style={{ gap: '16px', textAlign: 'left' }}>
            <button className="primary glow" onClick={doFacetLogin} disabled={loading} style={{ width: '100%' }}>
              {loading ? (
                <Loader2 size={16} className="spin" />
              ) : (
                <ExternalLink size={16} />
              )}
              {loading ? '跳转中...' : '使用 Facet 登录'}
            </button>

            {error && (
              <div style={{ color: 'var(--danger)', fontSize: '13px', textAlign: 'center' }}>{error}</div>
            )}

            <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '8px', textAlign: 'center' }}>
              当前已接入 Facet 统一身份中心
            </p>
          </div>
        ) : (
          <div className="flex flex-col" style={{ gap: '16px', textAlign: 'left' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                邮箱
              </label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@atelier.local"
                onKeyDown={(e) => e.key === 'Enter' && doLocalLogin()}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                密码
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="请输入密码"
                onKeyDown={(e) => e.key === 'Enter' && doLocalLogin()}
              />
              <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                默认管理员密码来自环境变量 ATELIER_ADMIN_PASSWORD
              </p>
            </div>

            {error && (
              <div style={{ color: 'var(--danger)', fontSize: '13px', textAlign: 'center' }}>{error}</div>
            )}

            <button
              className="primary glow"
              onClick={doLocalLogin}
              disabled={loading || !email.trim() || !password}
              style={{ marginTop: '8px', width: '100%' }}
            >
              {loading ? (
                <Loader2 size={16} className="spin" />
              ) : (
                <LogIn size={16} />
              )}
              {loading ? '登录中...' : '进入系统'}
            </button>

            <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '24px', textAlign: 'center' }}>
              当前为本地 JWT 认证，token 存储在本地浏览器
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
